import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { getMaps } from 'Plugins/FloorPlan/Utils/Room/MapByType';

import { QuestionContextHelper } from '../../../../Helpers/QuestionContextHelper';
import { QuestionContextType, QuestionStep, QuestionType } from '../../../../Models/Questions/QuestionDto';
import { MapContextState } from '../../../../Plugins/FloorPlan/Context/MapContext';
import { CompleteHelper } from '../../../../Plugins/FloorPlan/Helpers/CompleteHelper';
import { IMapItem } from '../../../../Plugins/FloorPlan/Models/IMapItem';
import { IOpening } from '../../../../Plugins/FloorPlan/Models/IOpening';
import { IRoom, RoomType } from '../../../../Plugins/FloorPlan/Models/IRoom';
import { IRoomItem } from '../../../../Plugins/FloorPlan/Models/IRoomItem';
import { EnhancedViewState } from '../../../Hooks/ViewState/ViewState';
import { SelectItemOption, useRoomInfosValues } from '../Components/Pannels/Rooms/Controllers/RoomInfosValues';
import { useHabitationContextController } from '../Controllers/HabitationContextController';
import { QuestionPrepare } from '../Helpers/QuestionPrepare';
import { QuoteState } from '../QuoteState';
import { CreateMapItemContextParams, QuestionContextUtils } from './QuestionContextUtils';

export type UpdateMapItemQuestionsParams = {
    mapState: Partial<MapContextState>;
    quoteState: EnhancedViewState<QuoteState>;
    isVendeur?: boolean;
};

export const useUpdateMapItemQuestions = () => {
    const roomInfosData = useRoomInfosValues();
    const habitation = useHabitationContextController();

    const toUpdateMapItemQuestions = ({
        mapState,
        quoteState,
        isVendeur,
    }: UpdateMapItemQuestionsParams): EnhancedViewState<MapContextState> => {
        //!\ WHEN UPDATE rooms, openings, roomItems, from Panels
        const { questions = [], stepHabitation } = quoteState;
        const { rooms = [], global_surface_area } = mapState;
        const { openings, roomItems } = MapHelper.getRoomAllElements(rooms);

        if (rooms.length || openings.length || roomItems.length) {
            rooms.forEach((room: IRoom) => {
                resetRoomItemInfo(room, {
                    roomTypes: roomInfosData.getRoomTypes(
                        room.localisation,
                        habitation.getUnitType(stepHabitation?.questions),
                        room.type
                    ),
                });
                const createContextParams: CreateMapItemContextParams = { global_surface_area };
                QuestionPrepare.prepareStepsQuestions(room, { ...createContextParams, stepHabitation, questions });
                QuestionPrepare.setFirstIncompatibility(room, rooms);
            });

            openings.forEach((opening: IOpening) => {
                const room = rooms.find((r) => r.roomId === opening.roomId)!;
                const { questionProduitPrincipal, } = getProduitPrincipal(room);
                const createContextParams: CreateMapItemContextParams = { global_surface_area };
                opening.questionsBaseContext = QuestionContextUtils.createOpeningContext(opening, room, createContextParams);
                QuestionPrepare.updateItemStepsQuestions({
                    itemId: opening.openingId!,
                    item: opening,
                    questions,
                    questionProduitPrincipal,
                    contextType: QuestionContextType.Openings,
                    openingType: opening.type,
                    isVendeur,
                });
                QuestionPrepare.setFirstIncompatibility(room, rooms);
                opening.completionStatus = CompleteHelper.isOpeningComplete(opening, openings);
            });

            roomItems.forEach((roomItem: IRoomItem) => {
                const room = rooms.find((r) => r.roomId === roomItem.roomId)!;
                const { questionProduitPrincipal } = getProduitPrincipal(room);

                const createContextParams: CreateMapItemContextParams = { global_surface_area };
                roomItem.questionsBaseContext = QuestionContextUtils.createRoomItemContext(roomItem, room, createContextParams);
                QuestionPrepare.updateItemStepsQuestions({
                    itemId: roomItem.roomItemId!,
                    item: roomItem,
                    questions,
                    questionProduitPrincipal,
                    contextType: QuestionContextType.RoomElements,
                    roomItemType: roomItem.type,
                    isVendeur,
                });
                QuestionPrepare.setFirstIncompatibility(roomItem, rooms);
                roomItem.completionStatus = CompleteHelper.isRoomItemComplete(roomItem, roomItems);
            });

            rooms.forEach((item: IRoom) => (item.completionStatus = CompleteHelper.isRoomComplete(item, rooms)));

            const roomsCompleted = CompleteHelper.allComplete(rooms);
            return { modified: true, rooms, roomsCompleted };
        }
        return { modified: false, roomsCompleted: false };
    };

    return { toUpdateMapItemQuestions };
};

export const getProduitPrincipal = (item: IMapItem) => {
    const firstQuestionProduit = item.questionsProduit?.questions?.find((q) => q.type === QuestionType.Produit && q.showQuestion)!;
    const produitPrincipal = firstQuestionProduit?.produitValue;
    return { questionProduitPrincipal: firstQuestionProduit, produitPrincipal };
}

export const getProduitSecondaire = (item: IMapItem) => {
    const secondQuestionProduit = item.questionsPose?.questions?.find((q) => q.type === QuestionType.Produit && q.showQuestion)!;
    const produitSecondaire = secondQuestionProduit?.produitValue;
    return { questionProduitSecondaire: secondQuestionProduit, produitSecondaire };
}

export const toUpdateGeneralQuestions = (
    mapState: Partial<MapContextState>,
    quoteState: QuoteState,
    isVendeur?: boolean
): EnhancedViewState<QuoteState> => {
    const { stepHabitation, stepServices, stepSynthese } = quoteState;
    const { sol, wall, stairs } = getMaps(mapState);

    const stepHabitationContext = QuestionContextHelper.getBaseQuestionsContext(stepHabitation?.questions);
    const optionsBaseContext = QuestionContextHelper.mergeContext(stepHabitationContext, {
        evaluations: {
            [QuestionContextHelper.ragreageObligatoreKey]: QuestionContextHelper.isRagreageObligatoireExist(
                mapState.rooms
            )
                ? QuestionContextHelper.ragreageObligatoreValue
                : '',
        },
        rooms: sol.rooms.map((room) => QuestionContextUtils.getLastStepContext(room, QuestionStep.StepFinitions)),
        walls: wall.rooms.map((room) => QuestionContextUtils.getLastStepContext(room, QuestionStep.StepFinitions)),
        stairs: stairs.rooms.map((room) => QuestionContextUtils.getLastStepContext(room, QuestionStep.StepFinitions)),
        total_area: mapState.global_surface_area,
    });
    QuestionPrepare.updateGeneralStepsQuestions({
        contextQuestionList: stepServices!,
        previousContext: optionsBaseContext,
        isVendeur,
    });

    const lastServiceQestionContext = QuestionContextHelper.getBaseQuestionsContext(stepServices?.questions);
    QuestionPrepare.updateGeneralStepsQuestions({
        contextQuestionList: stepSynthese!,
        isVendeur,
        previousContext: lastServiceQestionContext || optionsBaseContext,
    });

    return { ...quoteState, modified: true, stepServices, stepSynthese };
};

export const getStepServiceBaseContext = (mapState: Partial<MapContextState>, quoteState: QuoteState) => {
    const { stepHabitation } = quoteState;
    const { sol, wall } = getMaps(mapState);

    const stepHabitationContext = QuestionContextHelper.getBaseQuestionsContext(stepHabitation?.questions);
    const optionsBaseContext = QuestionContextHelper.mergeContext(stepHabitationContext, {
        evaluations: {
            [QuestionContextHelper.ragreageObligatoreKey]: QuestionContextHelper.isRagreageObligatoireExist(
                mapState.rooms
            )
                ? QuestionContextHelper.ragreageObligatoreValue
                : '',
        },
        rooms: sol.rooms.map((room) => QuestionContextUtils.getLastStepContext(room, QuestionStep.StepFinitions)),
        walls: wall.rooms.map((room) => QuestionContextUtils.getLastStepContext(room, QuestionStep.StepFinitions)),
    });
    return optionsBaseContext;
};

export const getStepSyntheseBaseContext = (mapState: Partial<MapContextState>, quoteState: QuoteState) => {
    const { stepServices } = quoteState;
    const optionsBaseContext = getStepServiceBaseContext(mapState, quoteState);
    const lastServiceQestionContext = QuestionContextHelper.getBaseQuestionsContext(stepServices?.questions);
    return lastServiceQestionContext || optionsBaseContext;
};

type ResetRoomItemInfoParams = { roomTypes: SelectItemOption[] };
const resetRoomItemInfo = (room: IRoom, params: ResetRoomItemInfoParams) => {
    const isRoomTypeValid = (roomType: string) => {
        const { roomTypes = [] } = params;
        return Boolean(roomTypes.find((x) => x.label === roomType));
    };
    room.roomType = isRoomTypeValid(room.roomType!) ? room.roomType : undefined;
};

export const toQuestionContextType = (roomType: RoomType): QuestionContextType => {
    switch (roomType) {
        case RoomType.Sol:
            return QuestionContextType.Rooms;
        case RoomType.Wall:
            return QuestionContextType.Walls;
        case RoomType.Stairs:
            return QuestionContextType.Stairs;
        default:
            return QuestionContextType.Rooms; // Default case if needed
    }
}